import { apiFetch } from "../utils/apiFetch";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import PlatformCheckoutConfirmation from "./PlatformCheckoutConfirmation";
import { useAuth } from "../context/AuthContext";
import { ownerPlan, publicPlans } from "../utils/communityEntitlements.mjs";
import { FiCheck, FiUsers, FiZap, FiAward, FiLoader } from "react-icons/fi";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true));
      existingScript.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });

const ChoosePlan = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirmCheckout = id => { setSelectedPlan(null); setSearchParams(previous => { const next = new URLSearchParams(previous); next.set("checkout", id); return next; }, { replace: true }); };
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [cancellation, setCancellation] = useState(null);
  const [subscriptionEnded, setSubscriptionEnded] = useState(false);
  const { userInfo, userId } = useAuth();
  const subscription = subscriptionDetails || userInfo?.subscriptionInfo;
  const hasEnded = subscriptionEnded || ["cancelled", "completed", "expired"].includes(subscription?.status);
  const cancellationPending = cancellation && ["requested", "confirmed", "unconfirmed"].includes(cancellation.state);
  const canCancel = subscription?.providerSubscriptionId && !subscriptionEnded &&
    !["cancelled", "completed", "expired"].includes(subscription.status);

  useEffect(() => {
    const controller = new AbortController();
    if (!userId) return;
    const loadSubscription = async () => {
      try {
        const response = await apiFetch(`${process.env.API_URL}/api/subscriptions/me`, {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not load subscription");
        if (!controller.signal.aborted) {
          setSubscriptionDetails(data.subscription);
          setCancellation(data.cancellation);
          setSubscriptionEnded(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) setError(error.message);
      }
    };
    loadSubscription();
    return () => controller.abort();
  }, [userId, userInfo?.subscriptionInfo?.providerSubscriptionId, userInfo?.subscriptionInfo?.status]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiFetch(`${process.env.API_URL}/api/plans`, {
          headers: {
            "Content-Type": "application/json",

          },
        });

        const plansData = await res.json();

        if (!res.ok) {
          throw new Error(plansData.message || "Could not get plans");
        }

        setPlans(publicPlans(plansData));
      } catch (err) {
        setError(err.message);
        setPlans([]);
      }
    };
    fetchPlans();
  }, []);

  const startCheckout = async (plan) => {
    if (plan.id === "default") {
      navigate("/courses");
      return;
    }
    if (selectedPlan || (canCancel && cancellationPending)) return;

    setSelectedPlan(plan.id);
    setError("");
    setNotice("");

    try {
      if (!userId) {
        navigate("/login", { replace: true });
        return;
      }

      const endpoint = "/api/subscriptions/paid";

      const response = await apiFetch(`${process.env.API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",

        },
        body: JSON.stringify({ plan: plan.id }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Could not create subscription");
      }
      if (data.pending || data.confirmed) { confirmCheckout(data.providerSubscriptionId); return; }

      if (["plan_upgraded", "plan_change_scheduled", "plan_change_processing"].includes(data.action)) {
        setNotice(data.message);
        setSelectedPlan(null);
        return;
      }

      const razorpayLoaded = await loadRazorpay();
      if (!razorpayLoaded) throw new Error("Could not load Razorpay Checkout");

      if (!data.providerSubscriptionId) { setNotice(data.message || "Subscription updated."); setSelectedPlan(null); return; }
      let submitted = false;
      const checkout = new window.Razorpay({
        key: data.keyId,
        subscription_id: data.providerSubscriptionId,
        name: "Qronus",
        description:
          plan.id === "basic" &&
          userInfo?.subscriptionInfo?.status === "default"
            ? "Basic plan: 30 days free, then ₹499/month"
            : `${plan.name} plan: ₹${(plan.amount / 100).toLocaleString("en-IN")}/month`,

        prefill: {
          name: userInfo?.username,
          email: userInfo?.email,
          contact: "9441101879",
        },

        theme: {
          color: "#111827",
        },
        modal: {
          confirm_close: true,
          escape: false,
          backdropclose: false,
          animation: true,
          ondismiss: () => {
            if (!submitted) confirmCheckout(data.providerSubscriptionId);
          },
        },

        handler: (payment) => {
          submitted = true;
          navigate("/subscription-verification", {
            replace: true,
            state: {
              payment: {
                razorpay_payment_id: payment.razorpay_payment_id,
                razorpay_subscription_id: payment.razorpay_subscription_id,
                razorpay_signature: payment.razorpay_signature,
              },
            },
          });
        },
      });

      checkout.on("payment.failed", (failure) => {
        setError(failure?.error?.description || "Payment authorization failed");
      });

      checkout.open();

      // checkout.once("ready", (response) => {
      //   setMethods(response.methods);
      // });
    } catch (checkoutError) {
      setError(checkoutError.message);
      setSelectedPlan(null);
    }
  };

  if (searchParams.get("checkout")) return <PlatformCheckoutConfirmation subscriptionId={searchParams.get("checkout")} onDone={state => {
    setSearchParams(previous => { const next = new URLSearchParams(previous); next.delete("checkout"); return next; }, { replace: true });
    setError(""); setSelectedPlan(null);
    if (state === "confirmed" || state === "attention") navigate("/settings?setting=subscription", { replace: true });
  }} />;

  return (
    <main className="min-h-screen bg-[#f6f8f8] text-gray-950">
      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {notice && <p role="status" className="mb-4 text-sm text-orange-700">{notice}</p>}
        {error && (
          <div
            role="alert"
            className="mb-6 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <div className="grid gap-5 md:grid-cols-3">
          {!plans ? (
            <div role="status" aria-label="Loading plans" className="col-span-full flex min-h-64 items-center justify-center">
              <FiLoader aria-hidden="true" className="h-7 w-7 animate-spin text-orange-700" />
            </div>
          ) : (
            plans.map((plan) => {
              const loading =
                selectedPlan === plan.id;
              const isCurrentPlan =
                plan.id === (hasEnded ? "default" : ownerPlan(subscription));
              // const disabled = Boolean(selectedPlan) && !loading;
              const disabled =
                loading || Boolean(selectedPlan) || isCurrentPlan || Boolean(canCancel && cancellationPending);
              const PlanIcon = plan.id === "default" ? FiUsers : plan.id === "plus" ? FiZap : FiAward;
              const iconStyle = plan.id === "default" ? "bg-blue-50 text-blue-700" : plan.id === "plus" ? "bg-orange-50 text-orange-700" : "bg-amber-50 text-amber-700";
              const buttonLabel = isCurrentPlan
                ? "Current plan"
                : plan.id === "default"
                  ? "Continue to channels"
                  : `Choose ${plan.name}`;

              return (
                <article
                  key={plan.id}
                  className={`relative flex min-w-0 flex-col rounded-lg border bg-white p-6 shadow-sm ${
                    plan.id === "plus" ? "border-orange-600 ring-1 ring-orange-600" : "border-gray-100"
                  }`}
                >
                  <div className="mb-6">
                    <div className="mb-5 flex h-10 items-center justify-between gap-3">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-md ${iconStyle}`}>
                        <PlanIcon aria-hidden="true" className="h-5 w-5" />
                      </span>

                      {plan.id === "plus" && (
                        <span className="rounded bg-orange-50 px-2 py-1 text-xs font-semibold text-orange-800">
                          Popular
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-bold">{plan.name}</h2>

                    <div className="mt-5 flex items-end gap-1">
                      <span className="text-3xl font-bold">
                        ₹{(plan.amount / 100).toLocaleString("en-IN")}
                      </span>
                      <span className="pb-1 text-sm text-gray-500">/month</span>
                    </div>

                    <p className="mt-3 min-h-10 text-sm text-gray-600">
                      {plan.description}
                    </p>
                  </div>

                  <ul className="mb-8 flex-1 space-y-3 border-t border-gray-100 pt-6 text-sm text-gray-700">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <FiCheck
                          aria-hidden="true"
                          className="mt-0.5 h-4 w-4 shrink-0 text-orange-700"
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    disabled={disabled}
                    aria-busy={loading}
                    aria-label={loading ? `Processing ${plan.name}` : buttonLabel}
                    onClick={() => startCheckout(plan)}
                    className={`flex h-11 w-full items-center justify-center rounded-md px-4 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:cursor-not-allowed ${
                      isCurrentPlan
                        ? "border border-gray-300 bg-white text-gray-800"
                        : plan.id === "plus"
                          ? "bg-orange-700 text-white enabled:hover:bg-orange-800"
                          : "bg-gray-950 text-white enabled:hover:bg-gray-800"
                    }`}
                  >
                    {loading ? <FiLoader aria-hidden="true" className="h-5 w-5 animate-spin" /> : buttonLabel}
                  </button>

                  {plan.trial && (
                    <p className="mt-3 text-center text-xs text-gray-500">
                      ₹499/month after 30 days. Cancel anytime.
                    </p>
                  )}
                </article>
              );
            })
          )}
        </div>
        {(canCancel || cancellation || hasEnded) && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-gray-100 pt-6">
            <div className="min-w-0 text-sm text-gray-600" aria-live="polite">
              <h2 className="font-semibold text-gray-950">Subscription renewal</h2>
              {hasEnded ? <p className="mt-1">Subscription ended.</p> : cancellationPending ? (
                <p className="mt-1">{cancellation.state !== "confirmed"
                  ? "Cancellation confirmation is pending. Contact support before retrying."
                  : cancellation.atCycleEnd && cancellation.effectiveAt
                    ? `Renewal cancelled. Current access ends ${new Date(cancellation.effectiveAt).toLocaleString()}.`
                    : "Subscription cancelled."}</p>
              ) : <p className="mt-1">Cancel renewal without losing the remainder of your paid billing cycle.</p>}
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default ChoosePlan;
